import React, { PureComponent } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { convertFromRaw } from 'draft-js';
import PropTypes from 'prop-types';

import DraftRenderer from '../../../utils/editor/lib/components/DraftRenderer';
import Graph from '../DraftEditor/GraphPlugin/Graph';
import Gallery from '../DraftEditor/GalleryPlugin/Gallery';
import Person from '../DraftEditor/PersonPlugin/Person';

import './styles.scss';

const customBlocks = {
  'usc-person': Person
};

const excludedComponent = {
  'usc-gallery': Gallery,
  'usc-graph': Graph
};

const makeOptions = contentState => {
  return {
    blockRenderers: {
      atomic: (block) => {
        const entityKey = block.getEntityAt(0);

        if (entityKey) {
          const entity = contentState.getEntity(entityKey);

          if (entity) {
            const Component = customBlocks[entity.getType()];

            if (Component) {
              return renderToStaticMarkup(
                <Component block={block} contentState={contentState} />
              );
            }
          }
        }
      }
    },
    blockStyleFn: block => {
      if (block.getType() === 'lead-paragraph') {
        return {
          attributes: {
            class: 'lead-paragraph'
          }
        };
      }
    },
    inlineStyles: {
      highlight: { style: { color: '#03b5b1' } }
    },
    entityStyleFn: (entity) => {
      const entityType = entity.get('type');
      if (entityType === 'usc-link') {
        const data = entity.getData();
        return {
          element: 'a',
          attributes: {
            href: data.src,
            target: '_blank',
            rel: 'noreferrer noopener'
          }
        };
      }
    }
  };
};

export default class DraftRendererWithOptions extends PureComponent {
  splitContent = content => {
    if (!content.trim()) {
      return [content];
    }

    const rawState = JSON.parse(content);
    const newBlockState = () => ({
      ...rawState,
      blocks: []
    });

    let createNewState = false;

    const newStates = rawState.blocks.reduce((arr, block) => {
      if (block.type !== 'atomic' ||
        !rawState.entityMap ||
        block.entityRanges.length !== 1 ||
        block.entityRanges[0].length !== 1
      ) {
        if (createNewState) {
          arr.push(newBlockState());
          createNewState = false;
        }

        arr[arr.length - 1].blocks.push(block);
        return arr;
      }

      const entity = rawState.entityMap[block.entityRanges[0].key];

      const isCustomBlock = Object.keys(excludedComponent)
        .indexOf(entity.type) !== -1;

      if (!!entity && isCustomBlock) {
        const galleryState = newBlockState();
        galleryState.blocks.push(block);
        galleryState.entityType = entity.type;

        arr.push(galleryState);
        createNewState = true;

        return arr;
      }

      if (createNewState) {
        arr.push(newBlockState());
        createNewState = false;
      }

      arr[arr.length - 1].blocks.push(block);
      return arr;
    }, [newBlockState()]);

    return newStates;
  };

  render() {
    const states = this.splitContent(this.props.content);

    return (
      <div className="draft-js-renderer">
        {states.map((state, i) => {
          if (!!state.entityType) {
            const CustomComponent = excludedComponent[state.entityType];
            const contentState = convertFromRaw(state);
            const block = contentState.getLastBlock();

            return (
              <CustomComponent
                key={i}
                contentState={contentState}
                block={block}
              />
            );
          }

          const content = typeof state === 'object'
            ? JSON.stringify(state)
            : state;

          return (
            <div key={i} className="draft-js-block">
              <div className="draft-js-container">
                <DraftRenderer
                  makeOptions={makeOptions}
                  content={content}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}

DraftRendererWithOptions.propTypes = {
  content: PropTypes.string
};



// WEBPACK FOOTER //
// ./src/components/widgets/DraftRenderer/index.js